import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RouteSuspense } from "@/components/RouteSuspense";
import { MainLayout } from "@/layouts/MainLayout";
import { PagePlaceholder } from "@/pages/StatusPage";
import { DynamicParentRedirect } from "@/routes/components/DynamicParentRedirect";
import { HomeRedirect } from "@/routes/components/HomeRedirect";
import { RoutePermissionGuard } from "@/routes/components/RoutePermissionGuard";
import { toRouteSegment } from "@/routes/menuConfig";
import { DETAIL_PAGE_LOADERS, getParentMenuHrefs, PAGE_LOADERS } from "@/routes/pageLoaders";

const LoginPage = lazy(() => import("@/pages/Login").then((m) => ({ default: m.LoginPage })));
const NotFoundPage = lazy(() =>
  import("@/pages/StatusPage").then((m) => ({ default: m.NotFoundPage })),
);
const ForbiddenPage = lazy(() =>
  import("@/pages/StatusPage").then((m) => ({ default: m.ForbiddenPage })),
);

/** 预构建 lazy 组件，避免 render 内重复 lazy() */
const LAZY_PAGES = Object.fromEntries(
  Object.entries(PAGE_LOADERS).map(([href, loader]) => [href, lazy(loader)]),
);

/** 详情子路由 lazy 组件 */
const LAZY_DETAIL_PAGES = Object.fromEntries(
  Object.entries(DETAIL_PAGE_LOADERS).map(([path, loader]) => [path, lazy(loader)]),
);

/** 从 href 生成占位页标题 */
const hrefToTitle = (href: string): string => {
  const segment = href.split("/").filter(Boolean).pop();
  return segment ?? href;
};

/** 按菜单 href 生成懒加载页面元素 */
const createPageElement = (href: string, title: string) => {
  const Page = LAZY_PAGES[href];
  if (!Page) return <PagePlaceholder title={title} />;
  return (
    <RouteSuspense>
      <Page />
    </RouteSuspense>
  );
};

/** 根据页面注册表生成主布局子路由（入口重定向 + 叶子页 + 父级重定向 + 403 + 404） */
const buildLayoutChildren = (): RouteObject[] => {
  const guardedChildren: RouteObject[] = [
    {
      index: true,
      element: <HomeRedirect />,
    },
  ];
  // 叶子页面：路径来自 pageLoaders，与动态侧栏菜单解耦
  for (const href of Object.keys(PAGE_LOADERS)) {
    guardedChildren.push({
      path: toRouteSegment(href),
      element: createPageElement(href, hrefToTitle(href)),
    });
  }
  // 详情子路由（如 /feedback/:id），权限由 RoutePermissionGuard 按父路径继承
  for (const path of Object.keys(DETAIL_PAGE_LOADERS)) {
    const Page = LAZY_DETAIL_PAGES[path];
    guardedChildren.push({
      path,
      element: (
        <RouteSuspense>
          <Page />
        </RouteSuspense>
      ),
    });
  }
  // 父菜单无独立页时，按用户菜单树跳转到第一个子菜单
  for (const parentHref of getParentMenuHrefs()) {
    guardedChildren.push({
      path: toRouteSegment(parentHref),
      element: <DynamicParentRedirect parentHref={parentHref} />,
    });
  }
  guardedChildren.push(
    {
      path: "403",
      element: (
        <RouteSuspense>
          <ForbiddenPage />
        </RouteSuspense>
      ),
    },
    {
      path: "*",
      element: (
        <RouteSuspense>
          <NotFoundPage />
        </RouteSuspense>
      ),
    },
  );
  return [
    {
      element: <RoutePermissionGuard />,
      children: guardedChildren,
    },
  ];
};

/** 应用路由表 */
export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RouteSuspense>
        <LoginPage />
      </RouteSuspense>
    ),
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: buildLayoutChildren(),
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);
